export class DOM {
  /**
   * Create an element with optional classes and attributes
   */
  static createElement<K extends keyof HTMLElementTagNameMap>(
    tag: K,
    options?: {
      className?: string;
      classes?: string[];
      id?: string;
      attributes?: Record<string, string>;
      styles?: Partial<CSSStyleDeclaration>;
      innerHTML?: string;
      textContent?: string;
    }
  ): HTMLElementTagNameMap[K] {
    const element = document.createElement(tag);
    
    if (options) {
      if (options.className) {
        element.className = options.className;
      }
      
      if (options.classes) {
        element.classList.add(...options.classes);
      }
      
      if (options.id) {
        element.id = options.id;
      }
      
      if (options.attributes) {
        Object.entries(options.attributes).forEach(([key, value]) => {
          element.setAttribute(key, value);
        });
      }
      
      if (options.styles) {
        Object.assign(element.style, options.styles);
      }
      
      if (options.innerHTML) {
        element.innerHTML = options.innerHTML;
      } else if (options.textContent) {
        element.textContent = options.textContent;
      }
    }
    
    return element;
  }

  /**
   * Query selector with type safety
   */
  static querySelector<T extends HTMLElement = HTMLElement>(
    selector: string,
    parent: Document | HTMLElement = document
  ): T | null {
    return parent.querySelector<T>(selector);
  }

  /**
   * Query selector all with type safety
   */
  static querySelectorAll<T extends HTMLElement = HTMLElement>(
    selector: string,
    parent: Document | HTMLElement = document
  ): NodeListOf<T> {
    return parent.querySelectorAll<T>(selector);
  }

  /**
   * Append multiple children to a parent
   */
  static appendChildren(parent: HTMLElement, ...children: (HTMLElement | string)[]): void {
    children.forEach(child => {
      if (typeof child === 'string') {
        parent.insertAdjacentHTML('beforeend', child);
      } else {
        parent.appendChild(child);
      }
    });
  }

  /**
   * Insert element before a reference node
   */
  static insertBefore(newNode: Node, referenceNode: Node): void {
    referenceNode.parentNode?.insertBefore(newNode, referenceNode);
  }

  /**
   * Insert element after a reference node
   */
  static insertAfter(newNode: Node, referenceNode: Node): void {
    referenceNode.parentNode?.insertBefore(newNode, referenceNode.nextSibling);
  }

  /**
   * Remove element from DOM
   */
  static removeElement(element: HTMLElement): void {
    element.remove();
  }

  /**
   * Empty an element (remove all children)
   */
  static emptyElement(element: HTMLElement): void {
    while (element.firstChild) {
      element.removeChild(element.firstChild);
    }
  }

  /**
   * Replace element with another
   */
  static replaceElement(oldElement: HTMLElement, newElement: HTMLElement): void {
    oldElement.replaceWith(newElement);
  }

  /**
   * Toggle class on element
   */
  static toggleClass(element: HTMLElement, className: string, force?: boolean): boolean {
    return element.classList.toggle(className, force);
  }

  /**
   * Add event listener with automatic cleanup
   */
  static addEventListener<K extends keyof HTMLElementEventMap>(
    element: HTMLElement | Document | Window,
    event: K,
    handler: (this: HTMLElement, ev: HTMLElementEventMap[K]) => any,
    options?: boolean | AddEventListenerOptions
  ): () => void {
    element.addEventListener(event as string, handler as EventListener, options);
    
    // Return cleanup function
    return () => {
      element.removeEventListener(event as string, handler as EventListener, options);
    };
  }

  /**
   * Delegate event handling
   */
  static delegate<K extends keyof HTMLElementEventMap>(
    parent: HTMLElement | Document,
    eventType: K,
    selector: string,
    handler: (event: HTMLElementEventMap[K], target: HTMLElement) => void
  ): () => void {
    const listener = (event: Event) => {
      const target = (event.target as HTMLElement).closest(selector);
      if (target && parent.contains(target)) {
        handler(event as HTMLElementEventMap[K], target as HTMLElement);
      }
    };
    
    parent.addEventListener(eventType as string, listener);
    
    return () => {
      parent.removeEventListener(eventType as string, listener);
    };
  }

  /**
   * Wait for element to appear in DOM
   */
  static waitForElement(
    selector: string,
    timeout: number = 5000
  ): Promise<HTMLElement> {
    return new Promise((resolve, reject) => {
      const element = document.querySelector<HTMLElement>(selector);
      if (element) {
        return resolve(element);
      }

      const observer = new MutationObserver((mutations, obs) => {
        const element = document.querySelector<HTMLElement>(selector);
        if (element) {
          obs.disconnect();
          resolve(element);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      setTimeout(() => {
        observer.disconnect();
        reject(new Error(`Element ${selector} not found within ${timeout}ms`));
      }, timeout);
    });
  }

  /**
   * Check if element is visible in viewport
   */
  static isInViewport(element: HTMLElement, partial: boolean = false): boolean {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;

    if (partial) {
      return (
        rect.bottom > 0 &&
        rect.right > 0 &&
        rect.top < windowHeight &&
        rect.left < windowWidth
      );
    }

    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= windowHeight &&
      rect.right <= windowWidth
    );
  }

  /**
   * Animate element using Web Animations API
   */
  static animate(
    element: HTMLElement,
    keyframes: Keyframe[] | PropertyIndexedKeyframes,
    options?: number | KeyframeAnimationOptions
  ): Animation {
    return element.animate(keyframes, options);
  }

  /**
   * Fade in element
   */
  static fadeIn(element: HTMLElement, duration: number = 300): Promise<void> {
    return new Promise(resolve => {
      element.style.opacity = '0';
      element.style.display = 'block';
      
      const animation = element.animate(
        [{ opacity: 0 }, { opacity: 1 }],
        { duration, fill: 'forwards' }
      );
      
      animation.onfinish = () => resolve();
    });
  }

  /**
   * Fade out element
   */
  static fadeOut(element: HTMLElement, duration: number = 300): Promise<void> {
    return new Promise(resolve => {
      const animation = element.animate(
        [{ opacity: 1 }, { opacity: 0 }],
        { duration, fill: 'forwards' }
      );
      
      animation.onfinish = () => {
        element.style.display = 'none';
        resolve();
      };
    });
  }

  /**
   * Slide down element
   */
  static slideDown(element: HTMLElement, duration: number = 300): Promise<void> {
    return new Promise(resolve => {
      element.style.overflow = 'hidden';
      const height = element.scrollHeight;
      
      const animation = element.animate(
        [
          { height: 0, opacity: 0 },
          { height: `${height}px`, opacity: 1 }
        ],
        { duration, fill: 'forwards' }
      );
      
      animation.onfinish = () => {
        element.style.height = 'auto';
        resolve();
      };
    });
  }

  /**
   * Slide up element
   */
  static slideUp(element: HTMLElement, duration: number = 300): Promise<void> {
    return new Promise(resolve => {
      element.style.overflow = 'hidden';
      const height = element.scrollHeight;
      
      const animation = element.animate(
        [
          { height: `${height}px`, opacity: 1 },
          { height: 0, opacity: 0 }
        ],
        { duration, fill: 'forwards' }
      );
      
      animation.onfinish = () => {
        element.style.display = 'none';
        resolve();
      };
    });
  }

  /**
   * Get element dimensions
   */
  static getDimensions(element: HTMLElement): {
    width: number;
    height: number;
    top: number;
    left: number;
    right: number;
    bottom: number;
  } {
    const rect = element.getBoundingClientRect();
    return {
      width: rect.width,
      height: rect.height,
      top: rect.top,
      left: rect.left,
      right: rect.right,
      bottom: rect.bottom
    };
  }

  /**
   * Get or set data attributes
   */
  static data(element: HTMLElement, key: string, value?: string): string | undefined {
    if (value !== undefined) {
      element.dataset[key] = value;
      return value;
    }
    return element.dataset[key];
  }

  /**
   * Observe element for intersection
   */
  static observeIntersection(
    element: HTMLElement,
    callback: (isIntersecting: boolean, entry: IntersectionObserverEntry) => void,
    options?: IntersectionObserverInit
  ): () => void {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        callback(entry.isIntersecting, entry);
      });
    }, options);
    
    observer.observe(element);
    
    return () => observer.disconnect();
  }

  /**
   * Observe element for resize
   */
  static observeResize(
    element: HTMLElement,
    callback: (entry: ResizeObserverEntry) => void
  ): () => void {
    const observer = new ResizeObserver((entries) => {
      entries.forEach(entry => callback(entry));
    });
    
    observer.observe(element);
    
    return () => observer.disconnect();
  }

  /**
   * Create document fragment from HTML string
   */
  static createFragment(html: string): DocumentFragment {
    const template = document.createElement('template');
    template.innerHTML = html.trim();
    return template.content;
  }

  /**
   * Ready state helper
   */
  static ready(callback: () => void): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
    } else {
      callback();
    }
  }

  /**
   * Debounce function for DOM events
   */
  static debounce<T extends (...args: any[]) => void>(
    func: T,
    wait: number
  ): (...args: Parameters<T>) => void {
    let timeout: ReturnType<typeof setTimeout>;
    
    return (...args: Parameters<T>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }

  /**
   * Throttle function for DOM events
   */
  static throttle<T extends (...args: any[]) => void>(
    func: T,
    limit: number
  ): (...args: Parameters<T>) => void {
    let inThrottle: boolean;
    
    return (...args: Parameters<T>) => {
      if (!inThrottle) {
        func(...args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }
}